import axios from 'axios'

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000/api'

const state = {
  conversations: [],
  currentConversation: null,
  messages: [],
  loading: false,
  error: null
}

const getters = {
  allConversations: state => state.conversations,
  currentConversation: state => state.currentConversation,
  messages: state => state.messages,
  loading: state => state.loading,
  error: state => state.error
}

const actions = {
  async fetchConversations({ commit }) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.get(`${API_URL}/characters/conversations/`)
      commit('SET_CONVERSATIONS', response.data)
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      console.error('Error fetching conversations:', error)
      commit('SET_ERROR', 'Failed to load conversations')
      return []
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchConversation({ commit }, id) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.get(`${API_URL}/characters/conversations/${id}/`)
      commit('SET_CURRENT_CONVERSATION', response.data)
      commit('SET_MESSAGES', response.data.messages)
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      console.error(`Error fetching conversation ${id}:`, error)
      commit('SET_ERROR', 'Failed to load conversation')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async createConversation({ commit, dispatch }, characterId) {
    commit('SET_LOADING', true)
    try {
      const response = await axios.post(`${API_URL}/characters/conversations/`, {
        character: characterId
      })
      commit('SET_CURRENT_CONVERSATION', response.data)
      commit('SET_MESSAGES', response.data.messages || [])
      commit('SET_ERROR', null)
      
      // Update the conversations list
      await dispatch('fetchConversations')
      
      return response.data
    } catch (error) {
      console.error('Error creating conversation:', error)
      commit('SET_ERROR', 'Failed to create conversation')
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async fetchMessages({ commit, state }) {
    if (!state.currentConversation) {
      commit('SET_ERROR', 'No active conversation')
      return []
    }
    
    commit('SET_LOADING', true)
    try {
      const response = await axios.get(
        `${API_URL}/characters/conversations/${state.currentConversation.id}/messages/`
      )
      commit('SET_MESSAGES', response.data)
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      console.error('Error fetching messages:', error)
      commit('SET_ERROR', 'Failed to load messages')
      return []
    } finally {
      commit('SET_LOADING', false)
    }
  },
  
  async sendMessage({ commit, state, dispatch }, content) {
    if (!state.currentConversation) {
      commit('SET_ERROR', 'No active conversation')
      return null
    }
    
    commit('SET_LOADING', true)
    try {
      const response = await axios.post(
        `${API_URL}/characters/conversations/${state.currentConversation.id}/messages/create/`,
        { 
          conversation: state.currentConversation.id,
          message_type: 'user',
          content
        }
      )
      
      // Fetch the updated conversation to get the AI response
      await dispatch('fetchConversation', state.currentConversation.id)
      
      commit('SET_ERROR', null)
      return response.data
    } catch (error) {
      if (error.response && error.response.status === 402) {
        commit('SET_ERROR', 'You have no free messages left. Subscribe to get more.')
      } else {
        console.error('Error sending message:', error)
        commit('SET_ERROR', 'Failed to send message')
      }
      return null
    } finally {
      commit('SET_LOADING', false)
    }
  }
}

const mutations = {
  SET_CONVERSATIONS(state, conversations) {
    state.conversations = conversations
  },
  
  SET_CURRENT_CONVERSATION(state, conversation) {
    state.currentConversation = conversation
  },
  
  SET_MESSAGES(state, messages) {
    state.messages = messages
  },
  
  SET_LOADING(state, loading) {
    state.loading = loading
  },
  
  SET_ERROR(state, error) {
    state.error = error
  }
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
} 